/* iter55z — Pinnable community chat dock.
 *
 * Streams new forum posts (SSE) from the user's accessible scopes and
 * lets them fire off a quick inline reply. Dock position (left, right,
 * bottom) is persisted to localStorage. Toggled from the ActivityBar
 * "Community" button below Marketing & Business.
 *
 * The dock is intentionally read-focused: it renders a live scrolling
 * feed with a "which thread is this from?" line above each message.
 * Composing a NEW thread still routes to the full /community page.
 */
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  MessagesSquare, X, PanelRight, PanelLeft, PanelBottom, Send, Loader2,
  Users, Crown, ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { forumApi } from '@/lib/api';

const POSITIONS = ['left', 'right', 'bottom'];
const STORAGE_KEY = 'pw_chat_dock_pos';

function loadPosition() {
  try {
    const p = window.localStorage.getItem(STORAGE_KEY);
    if (POSITIONS.includes(p)) return p;
  } catch { /* ignore */ }
  return 'right';
}

function relTime(iso) {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    const sec = Math.max(0, (Date.now() - t) / 1000);
    if (sec < 60) return 'just now';
    if (sec < 3600) return `${Math.floor(sec / 60)}m`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
    return `${Math.floor(sec / 86400)}d`;
  } catch { return ''; }
}

const SCOPE_META = {
  community: { label: 'Community',     icon: Users },
  pro_house: { label: 'House Writers', icon: Crown },
};

// Tiny deterministic HSL colour from a string so avatars stay stable
// across page reloads without needing to store one per user.
function avatarColor(seed) {
  let h = 0;
  for (let i = 0; i < (seed || '').length; i += 1) h = (h * 31 + seed.charCodeAt(i)) & 0xffffffff;
  return `hsl(${Math.abs(h) % 360}, 55%, 45%)`;
}
function initials(name) {
  const parts = (name || '').trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]).join('').toUpperCase().slice(0, 2) || '?';
}

// Render a post body with @mentions styled (basic — no XSS surface
// because we split on the regex and only inject `<b>` for matches).
function renderBody(text) {
  if (!text) return null;
  // Split with a GLOBAL regex captures every mention. But for the
  // per-segment `.test()` below we MUST use a NON-global sibling —
  // a global regex retains `lastIndex` between calls, which would
  // skip the second mention when two adjacent mentions appear back-
  // to-back (e.g. `@bob@carol`).
  const splitRe = /(@"[^"\n]{1,60}"|@[\w\-\.]{2,40})/g;
  const testRe  = /^(@"[^"\n]{1,60}"|@[\w\-\.]{2,40})$/;
  const parts = text.split(splitRe);
  return parts.map((seg, i) => {
    if (!seg) return null;
    const key = `${i}:${seg}`;
    if (testRe.test(seg)) {
      const clean = seg.replace(/^@"?|"?$/g, '');
      return <b key={key} className="rounded-sm bg-primary/10 px-0.5 text-primary">@{clean}</b>;
    }
    return <span key={key}>{seg}</span>;
  });
}

export function ChatDock({ open, onClose }) {
  const [position, setPosition] = useState(loadPosition);
  const [scope, setScope] = useState('all');            // 'all' | 'community' | 'pro_house'
  const [posts, setPosts] = useState([]);
  const [connected, setConnected] = useState(false);
  const [myScopes, setMyScopes] = useState(['community']);
  const [replyOpen, setReplyOpen] = useState(null);     // thread_id currently replying to
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  const feedRef = useRef(null);
  // iter55z-c — Persistent new-thread composer at the foot of the dock.
  const [topics, setTopics] = useState([]);              // accessible topics (both scopes)
  const [composeTopic, setComposeTopic] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composing, setComposing] = useState(false);

  // Persist position.
  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, position); } catch { /* ignore */ }
  }, [position]);

  // SSE stream lifecycle. Reconnects whenever `scope` changes.
  useEffect(() => {
    if (!open) return undefined;
    const url = forumApi.streamUrl(scope === 'all' ? null : scope);
    const es = new EventSource(url, { withCredentials: true });
    setConnected(false);
    es.addEventListener('hello', (ev) => {
      try {
        const d = JSON.parse(ev.data);
        setMyScopes(d.scopes || ['community']);
        setConnected(true);
      } catch { /* ignore */ }
    });
    es.addEventListener('post', (ev) => {
      try {
        const p = JSON.parse(ev.data);
        setPosts((prev) => {
          if (prev.some((x) => x.id === p.id)) return prev;
          const next = [...prev, p];
          return next.length > 200 ? next.slice(-200) : next;
        });
        // Auto-scroll to the newest message when the user is near the bottom.
        setTimeout(() => {
          const el = feedRef.current;
          if (!el) return;
          const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
          if (nearBottom) el.scrollTop = el.scrollHeight;
        }, 30);
      } catch { /* ignore */ }
    });
    es.onerror = () => {
      setConnected(false);
      // Let EventSource auto-retry; we just show a disconnected badge.
    };
    return () => es.close();
  }, [open, scope]);

  const submitReply = useCallback(async (thread_id) => {
    if (replyBody.trim().length < 2) {
      toast.error('Reply too short.');
      return;
    }
    setSending(true);
    try {
      await forumApi.reply(thread_id, replyBody);
      setReplyBody('');
      setReplyOpen(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reply.');
    } finally {
      setSending(false);
    }
  }, [replyBody]);

  const visibleScopeTabs = useMemo(() => ['all', ...myScopes], [myScopes]);

  // Load accessible topics once when the dock opens so the composer has
  // choices. Refreshed if the user's scope list changes at runtime.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    forumApi.topics()
      .then((d) => {
        if (!alive) return;
        const rows = (d.topics || []).filter((t) => (d.my_scopes || []).includes(t.scope || 'community'));
        setTopics(rows);
        setComposeTopic((prev) => prev || rows[0]?.slug || '');
      })
      .catch(() => { /* silent */ });
    return () => { alive = false; };
  }, [open, myScopes.length]);

  const submitNewThread = useCallback(async () => {
    if (composeBody.trim().length < 2) { toast.error('Say a bit more before posting.'); return; }
    if (!composeTopic) { toast.error('Pick a topic first.'); return; }
    setComposing(true);
    try {
      // Auto-derive a short title from the first line of the body so the
      // dock stays a single-input surface. If the user's body is a real
      // question or headline, that IS the title.
      const firstLine = composeBody.split('\n')[0].trim();
      const title = firstLine.slice(0, 120) || 'New thread';
      await forumApi.createThread(composeTopic, title, composeBody);
      setComposeBody('');
      toast.success('Posted to community.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to post.');
    } finally { setComposing(false); }
  }, [composeBody, composeTopic]);

  if (!open) return null;

  const dockClass = cn(
    'chat-dock fixed z-40 flex flex-col overflow-hidden border-panel-border bg-background shadow-panel-active',
    position === 'left'   && 'left-12 top-24 bottom-6 w-80 border-r border-y rounded-r-md',
    position === 'right'  && 'right-0 top-24 bottom-6 w-80 border-l border-y rounded-l-md',
    position === 'bottom' && 'bottom-6 left-12 right-0 h-72 border-t rounded-t-md',
  );

  return (
    <div className={dockClass} data-testid="chat-dock" data-position={position}>
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-panel-border bg-panel-header/70 px-2 py-1.5">
        <MessagesSquare className="h-3.5 w-3.5 text-primary" />
        <span className="text-[11.5px] font-semibold tracking-tight">Live Community</span>
        <span
          className={cn(
            'inline-block h-1.5 w-1.5 rounded-full',
            connected ? 'bg-emerald-500' : 'bg-amber-500',
          )}
          title={connected ? 'Live' : 'Reconnecting…'}
          data-testid="chat-dock-connection"
        />
        <div className="ml-auto flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => setPosition('left')}
            className={cn('rounded-sm p-1 hover:bg-secondary', position === 'left' && 'text-primary')}
            data-testid="chat-dock-pin-left"
            title="Pin to left"
          >
            <PanelLeft className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setPosition('right')}
            className={cn('rounded-sm p-1 hover:bg-secondary', position === 'right' && 'text-primary')}
            data-testid="chat-dock-pin-right"
            title="Pin to right"
          >
            <PanelRight className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setPosition('bottom')}
            className={cn('rounded-sm p-1 hover:bg-secondary', position === 'bottom' && 'text-primary')}
            data-testid="chat-dock-pin-bottom"
            title="Pin to bottom"
          >
            <PanelBottom className="h-3.5 w-3.5" />
          </button>
          <a
            href="/community"
            target="_blank"
            rel="noreferrer"
            className="rounded-sm p-1 hover:bg-secondary"
            data-testid="chat-dock-open-full"
            title="Open full forum"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <button
            type="button"
            onClick={onClose}
            className="rounded-sm p-1 hover:bg-destructive/10 hover:text-destructive"
            data-testid="chat-dock-close"
            title="Close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Scope tabs */}
      <div className="flex items-center gap-0.5 border-b border-panel-border px-2 py-1">
        {visibleScopeTabs.map((s) => {
          const meta = s === 'all'
            ? { label: 'All', icon: MessagesSquare }
            : SCOPE_META[s] || { label: s, icon: MessagesSquare };
          const Icon = meta.icon;
          const active = scope === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => { setScope(s); setPosts([]); }}
              data-testid={`chat-dock-scope-${s}`}
              className={cn(
                'inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[10.5px] transition',
                active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary',
              )}
            >
              <Icon className="h-3 w-3" /> {meta.label}
            </button>
          );
        })}
      </div>

      {/* Feed */}
      <div
        ref={feedRef}
        className="flex-1 overflow-auto scroll-thin px-2 py-2"
        data-testid="chat-dock-feed"
      >
        {posts.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-[11px] text-muted-foreground">
            {connected ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <div>Watching for new messages…</div>
                <div className="text-[10.5px]">Nothing yet — write the first line.</div>
              </>
            ) : (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <div>Connecting to live feed…</div>
              </>
            )}
          </div>
        )}
        {posts.map((p) => (
          <div
            key={p.id}
            className="mb-2 rounded-md border border-panel-border bg-card px-2 py-1.5 shadow-panel"
            data-testid={`chat-dock-post-${p.id}`}
          >
            <div className="flex items-start gap-1.5">
              <span
                className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white"
                style={{ backgroundColor: avatarColor(p.author_name || p.author_user_id || 'anon') }}
                data-testid={`chat-dock-avatar-${p.id}`}
                aria-hidden="true"
              >
                {initials(p.author_name)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                  <span className="truncate">
                    <b className="text-foreground">{p.author_name}</b>
                    {p.thread_scope === 'pro_house' && (
                      <Crown className="ml-1 inline h-2.5 w-2.5 text-amber-700" />
                    )}
                    {p.thread_title && <span> · in <i>{p.thread_title}</i></span>}
                  </span>
                  <span className="ml-1 shrink-0">{relTime(p.created_at)}</span>
                </div>
                <div className="mt-0.5 whitespace-pre-wrap break-words text-[12px] leading-snug">
                  {renderBody(p.body)}
                </div>
                {replyOpen === p.thread_id ? (
                  <div className="mt-1.5 space-y-1" data-testid={`chat-dock-reply-form-${p.thread_id}`}>
                    <Textarea
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                      rows={2}
                      placeholder="Reply inline… tip: @name to mention"
                      className="min-h-[44px] text-[12px]"
                      data-testid="chat-dock-reply-body"
                    />
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setReplyOpen(null); setReplyBody(''); }}
                        className="h-6 px-2 text-[10.5px]"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => submitReply(p.thread_id)}
                        disabled={sending}
                        className="h-6 gap-1 px-2 text-[10.5px]"
                        data-testid="chat-dock-reply-submit"
                      >
                        <Send className="h-3 w-3" /> Send
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setReplyOpen(p.thread_id); setReplyBody(`@${(p.author_name || '').split(' ')[0]} `); }}
                    className="mt-0.5 text-[10px] text-primary hover:underline"
                    data-testid={`chat-dock-reply-open-${p.thread_id}`}
                  >
                    Reply
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* iter55z-c — Persistent bottom composer. Always visible so an
          empty feed never leaves the user with nothing to click. */}
      <div className="border-t border-panel-border bg-panel-header/60 px-2 py-1.5" data-testid="chat-dock-composer">
        <div className="mb-1 flex items-center gap-1.5">
          <label htmlFor="chat-dock-composer-topic" className="text-[9px] uppercase tracking-wider text-muted-foreground">Post to</label>
          <select
            id="chat-dock-composer-topic"
            value={composeTopic}
            onChange={(e) => setComposeTopic(e.target.value)}
            disabled={topics.length === 0}
            data-testid="chat-dock-composer-topic"
            className="h-6 min-w-0 flex-1 rounded-sm border border-border bg-background px-1 text-[10.5px]"
          >
            {topics.map((t) => (
              <option key={t.slug} value={t.slug}>
                {t.scope === 'pro_house' ? '👑 ' : ''}{t.name}
              </option>
            ))}
            {topics.length === 0 && <option value="">Loading topics…</option>}
          </select>
        </div>
        <div className="flex gap-1">
          <Textarea
            value={composeBody}
            onChange={(e) => setComposeBody(e.target.value)}
            onKeyDown={(e) => {
              // Cmd/Ctrl + Enter fires post — keeps the dock chat-native.
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                submitNewThread();
              }
            }}
            rows={2}
            placeholder="Start a new thread… ⌘/Ctrl + Enter to post"
            className="min-h-[44px] flex-1 text-[12px]"
            data-testid="chat-dock-composer-body"
          />
          <Button
            size="sm"
            onClick={submitNewThread}
            disabled={composing || !composeBody.trim() || !composeTopic}
            className="h-auto gap-1 self-stretch px-2 text-[10.5px]"
            data-testid="chat-dock-composer-submit"
            title="Post to community (⌘/Ctrl + Enter)"
          >
            <Send className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ChatDock;
